/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    COMPREHENSIVE_OPTIONAL_FIELDS_FIX
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Fixed optional field deletion bug by adding cache-busting to profile requests.
 *
 * Related Screens / Modules:
 * - client/src/services/usersService.ts
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    DEBUG_OPTIONAL_FIELDS_20251229
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Added comprehensive debug logging to track optional fields deletion bug.
 *
 * Related Screens / Modules:
 * - client/src/services/usersService.ts
 */
import axios from "axios";
// ... (existing imports)
import { User, LoginCredentials } from "../interfaces/User";
import { apiUrl } from "../config/environment";
import { getAuthHeaders } from "../utils/authUtils";

const api: string = `${apiUrl}/api/users`;
const authApi: string = `${apiUrl}/api/auth`;
const userPrefsApi: string = `${apiUrl}/api/user`;

// login
export function checkUser(credentials: LoginCredentials) {
  console.log("\n=== CLIENT LOGIN DEBUG ===");
  console.log("API URL from env:", process.env.REACT_APP_API_URL);
  console.log("Computed API base:", apiUrl);
  console.log("Full request URL:", `${api}/login`);
  console.log("Payload keys:", Object.keys(credentials));
  console.log(
    "Email present:",
    !!credentials.email,
    credentials.email ? `(${credentials.email})` : "",
  );
  console.log("Password present:", !!credentials.password);
  console.log("==========================\n");

  return axios.post(
    `${api}/login`,
    {
      email: credentials.email,
      password: credentials.password,
    },
    {
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
}

// register
export function addUser(user: User) {
  // [2025-12-20] Tracing: Import and capture client device info
  const { captureClientDeviceInfo } = require('../utils/captureClientDeviceInfo');
  const clientDeviceInfo = captureClientDeviceInfo();
  
  return axios.post(api, { ...user, clientDeviceInfo });
}

// profile
export function getUserProfile() {
  // [2025-12-29] Bug fix: Add cache-busting to ensure we always get fresh data
  return axios.get(`${api}/profile`, {
    ...getAuthHeaders(),
    params: { _t: Date.now() }
  });
}

/** BR-26: Broadcast readiness (GET /api/users/profile/broadcast-status) */
export function getBroadcastStatus() {
  return axios.get(`${api}/profile/broadcast-status`, getAuthHeaders());
}

// save/update profile (creates or updates current user's profile)
export function saveUserProfile(payload: Partial<User>, options?: { draft?: boolean }) {
  const params = new URLSearchParams();
  if (options?.draft) params.set("draft", "1");
  const url = params.toString() ? `${api}/profile?${params.toString()}` : `${api}/profile`;
  
  return axios.post(url, payload, getAuthHeaders());
}

/**
 * Fetch public profile of any user by ID
 * Used for viewing seller profiles in modals
 * @param userId - User ID to fetch
 * @returns User profile data
 */
// [2025-12-13] Profile Modal: Add getUserById for fetching seller profiles
export function getUserById(userId: string) {
  // [2025-12-29] Bug fix: Add cache-busting
  return axios.get(`${api}/${userId}`, {
    ...getAuthHeaders(),
    params: { _t: Date.now() }
  });
}

/** [Phase 7] BR-34: Get user preferences (e.g. dismissed_banners) */
export function getPreferences() {
  return axios.get<{ preferences: { dismissed_banners?: string[] } }>(
    `${userPrefsApi}/preferences`,
    getAuthHeaders()
  );
}

/** [Phase 7] BR-34: Dismiss an info banner by id */
export function dismissBanner(bannerId: string) {
  return axios.post<{ message: string; bannerId: string }>(
    `${userPrefsApi}/preferences/dismiss-banner`,
    { bannerId },
    getAuthHeaders()
  );
}

// [2026-03-09] GDPR: Cookie consent server sync
export function getCookieConsentFromServer() {
  return axios.get<{ cookieConsent: { value: string; updatedAt: string } | null }>(
    `${userPrefsApi}/preferences/cookie-consent`,
    getAuthHeaders()
  );
}

export function syncCookieConsentToServer(value: "accepted" | "declined") {
  return axios.post(
    `${userPrefsApi}/preferences/cookie-consent`,
    { value },
    getAuthHeaders()
  );
}

// [2026-06-23] KEEP_UNVERIFIED_LEADS: resend email verification link.
// Backend endpoint already exists (rate-limited, 60s cooldown).
export function resendVerificationEmail(email: string) {
  return axios.post(`${authApi}/verify/resend`, { email });
}

// password reset - request reset email
export function requestPasswordReset(email: string) {
  return axios.post(`${authApi}/password/forgot`, { email });
}

// password reset - complete reset with token
export function resetPassword(token: string, newPassword: string) {
  return axios.post(`${authApi}/password/reset`, { token, newPassword });
}
